"use client";

import { useState, useMemo } from "react";

import { type Prisma } from "@/.zenstack/logical-prisma-client";
import { ListingLink } from "@/components/ListingLink";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { MediaRenderer } from "@/components/ui/media-renderer-wrapper";

export const LISTING_CARD_SELECT = {
  title: true,
  slug: true,
  id: true,
  status: true,
  details: true,
  files: {
    select: {
      id: true,
      url: true,
      orderIndex: true,
      type: true,
      name: true,
    },
  },
  atAuction: {
    select: {
      startingBid: true,
      auctionEndDate: true,
      bids: {
        select: {
          currentBidAmount: true,
        },
      },
    },
  },
} as const;

export type ListingCardType = Prisma.ListingGetPayload<{
  select: typeof LISTING_CARD_SELECT;
}>;

export interface ListingCardProps {
  listing: ListingCardType;
}

export function ListingCard({ listing }: ListingCardProps) {
  const [imageError] = useState(false);

  const primaryFile = useMemo(() => {
    const files = listing.files ?? [];
    const firstWithUrl = files.find((file) => file?.url);
    return firstWithUrl ?? null;
  }, [listing.files]);

  const showMedia = Boolean(primaryFile) && !imageError;

  const auction = listing.atAuction ?? null;
  const topBid = auction?.bids?.[0]?.currentBidAmount ?? null;
  const priceToShow = topBid ?? auction?.startingBid ?? null;

  return (
    <Card className="relative overflow-hidden rounded-none p-4">
      <ListingLink listing={listing}>
        <CardHeader className="p-0">
          <div className="relative aspect-[4/3] overflow-hidden">
            {showMedia && primaryFile ? (
              <div className="h-full w-full transition-transform hover:scale-105">
                <MediaRenderer
                  file={{
                    url: primaryFile.url,
                    type: primaryFile.type,
                    name: primaryFile.name,
                  }}
                  alt={listing.title}
                  width={400}
                  height={300}
                  className="h-full w-full"
                  controls={false} // Disable video controls in card preview
                />
              </div>
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-muted">
                <span className="text-sm text-muted-foreground">No media</span>
              </div>
            )}

            {/* Overlay content - Status and Saved button */}
            <div className="pointer-events-none absolute top-2 left-2 z-10 flex flex-col items-start gap-2">
              {auction && (
                <>
                  <div className="pointer-events-auto">
                    <Badge
                      variant="outline"
                      className="border border-primary bg-background font-medium"
                    >
                      {new Date(auction.auctionEndDate) > new Date()
                        ? "ACTIVE"
                        : "Ended"}
                    </Badge>
                  </div>
                  <div className="pointer-events-auto">
                    <span className="border border-primary bg-background px-2 py-1 font-mono text-xs font-medium text-foreground">
                      Ends{" "}
                      {new Date(auction.auctionEndDate).toLocaleDateString()}
                    </span>
                  </div>
                </>
              )}
            </div>
            {/* Saved button - top right */}
            {/* <div className="pointer-events-none absolute top-2 right-2 z-10">
              <div
                className="pointer-events-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <SavedListingButton
                  variant="link"
                  size="sm"
                  className="relative h-8 w-8 p-0 bg-background border border-primary"
                  listingId={listing.id}
                  showLabel={false}
                />
              </div>
            </div> */}
          </div>
        </CardHeader>
        <CardContent className="pt-2">
          <h3 className="text-md mb-1 line-clamp-1 font-medium">
            {listing.title}
          </h3>
          <p className="mb-3 line-clamp-1 font-mono text-xs text-muted-foreground">
            {listing.details?.address
              ? `${listing.details.address.streetLine1}, ${listing.details.address.city}`
              : "Address not available"}
          </p>
          {priceToShow && (
            <div className="mb-3 flex items-center gap-2">
              <p className="text-lg font-medium">
                ${Number(priceToShow).toLocaleString()}
              </p>
              <div className="text-xs text-muted-foreground">
                {listing.details?.housingDetails?.bedrooms
                  ? `(${listing.details.housingDetails.bedrooms} ${listing.details.housingDetails.bedrooms === "1" ? "bed" : "beds"}`
                  : ""}
                {listing.details?.housingDetails?.bathrooms
                  ? `, ${listing.details.housingDetails.bathrooms} ${listing.details.housingDetails.bathrooms === "1" ? "bath" : "baths"})`
                  : ""}
              </div>
            </div>
          )}
        </CardContent>
      </ListingLink>
    </Card>
  );
}
